import { post, request } from '../api';

/** Mirrors the view types of `apps/api/src/approvals/*`; money always crosses the wire as a string. */

export type ApprovalKind = 'plan' | 'step' | 'external_action' | 'new_agent' | 'knowledge';
export type ApprovalDecision = 'approved' | 'rework' | 'rejected';
export type ApprovalChannel = 'panel' | 'telegram' | 'mcp';
export type ApprovalRisk = 'low' | 'medium' | 'high';
export type ApprovalTab = 'pending' | 'decided';
/** Server sort names of the decided table; the column keys are the same strings. */
export type ApprovalSort = 'requestedAt' | 'decidedAt' | 'kind';

export interface ApprovalSubject {
  type: string;
  id: string;
  executionId: string | null;
  taskId: string | null;
  taskKey: string | null;
  agentName: string | null;
  /** `cost_threshold`, `irreversible_tool`, `retry_in_flight`, … */
  reason: string | null;
  toolName?: string;
  args?: unknown;
  text?: string;
  textTruncated?: boolean;
  artifactId?: string;
  estimatedCost?: string;
  threshold?: string | null;
  cost?: string;
  attempt?: number;
}

export interface Approval {
  id: string;
  kind: ApprovalKind;
  risk: ApprovalRisk;
  business: { id: string; name: string } | null;
  requestedAt: string;
  expiresAt: string | null;
  ageMinutes: number;
  subject: ApprovalSubject;
  decision: ApprovalDecision | null;
  decidedAt: string | null;
  approver: { id: string; name: string } | null;
  channel: ApprovalChannel | null;
  reason: string | null;
  /** No decision and past `expiresAt`: shown as "протерміновано" among the decided. */
  expired: boolean;
}

export interface ApprovalMetrics {
  pending: number;
  oldestPendingMinutes: number | null;
  decidedLast7Days: number;
  byKind: Record<string, number>;
}

export interface ApprovalListResult {
  rows: Approval[];
  total: number;
}

export interface DecideResult {
  approvalId: string;
  kind: ApprovalKind;
  decision: ApprovalDecision;
  executionId: string | null;
  newExecutionId?: string;
  /** The sidebar badge after this decision. */
  pending: number;
}

export interface ApprovalListQuery {
  decision?: ApprovalTab;
  kind?: ApprovalKind;
  businessId?: string;
  sort?: ApprovalSort;
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

function qs(query: ApprovalListQuery): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== '') params.set(k, String(v));
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const approvalsApi = {
  list: (query: ApprovalListQuery = {}) => request<ApprovalListResult>(`/approvals${qs(query)}`),
  get: (id: string) => request<Approval>(`/approvals/${id}`),
  metrics: () => request<ApprovalMetrics>('/approvals/metrics'),
  businesses: () => request<{ id: string; name: string; status: string }[]>('/approvals/businesses'),
  decide: (id: string, decision: ApprovalDecision, reason?: string) => post<DecideResult>(`/approvals/${id}/decide`, { decision, ...(reason ? { reason } : {}) }),
};
